import React, { useState, useEffect } from 'react';
import './EditRestaurantModal.css';

const EditRestaurantModal = ({ restaurant, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    city: '',
    address: '',
    phone: [],
    shortDescription: '',
    isActive: true,
    isOpen: true,
    hasCafe: false,
    innerDelivery: false,
    valueAdded: false,
    logo: null,
    images: [],
    activeHours: {},
    socialMediaAccounts: {},
    facilities: [],
    subCategory: '',
    totalTables: 0,
    slug: '',
    payId: ''
  });

  const [logoPreview, setLogoPreview] = useState('');
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (restaurant) {
      setFormData({
        name: restaurant.name || '',
        category: restaurant.category || '',
        city: restaurant.city || '',
        address: restaurant.address || '',
        phone: Array.isArray(restaurant.phone) ? [...restaurant.phone] : [restaurant.phone || ''],
        shortDescription: restaurant.shortDescription || '',
        isActive: restaurant.isActive !== undefined ? restaurant.isActive : true,
        isOpen: restaurant.isOpen !== undefined ? restaurant.isOpen : true,
        hasCafe: restaurant.hasCafe || false,
        innerDelivery: restaurant.innerDelivery || false,
        valueAdded: restaurant.valueAdded || false,
        activeHours: restaurant.activeHours || {},
        socialMediaAccounts: restaurant.socialMediaAccounts || {},
        facilities: restaurant.facilities || [],
        subCategory: restaurant.subCategory || '',
        totalTables: restaurant.totalTables || 0,
        slug: restaurant.slug || '',
        payId: restaurant.payId || ''
      });

      if (restaurant.logo) {
        setLogoPreview(restaurant.logo);
      }
      if (restaurant.images && restaurant.images.length > 0) {
        setImagePreviews(restaurant.images);
      }
    }
  }, [restaurant]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePhoneChange = (index, value) => {
    const newPhones = [...formData.phone];
    newPhones[index] = value;
    setFormData(prev => ({
      ...prev,
      phone: newPhones
    }));
  };

  const addPhoneField = () => {
    setFormData(prev => ({
      ...prev,
      phone: [...prev.phone, '']
    }));
  };

  const removePhoneField = (index) => {
    if (formData.phone.length <= 1) return;
    const newPhones = formData.phone.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      phone: newPhones
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 300 * 1024) {
      setError('حجم لوگو نباید بیشتر از ۳۰۰ کیلوبایت باشد');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      setFormData(prev => ({
        ...prev,
        logo: file
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 10) {
      setError('حداکثر ۱۰ تصویر می‌توانید آپلود کنید');
      return;
    }

    const validFiles = files.filter(file => file.size <= 500 * 1024);
    if (validFiles.length !== files.length) {
      setError('هر تصویر حداکثر می‌تواند ۵۰۰ کیلوبایت باشد');
      return;
    }

    const newImagePreviews = [];
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newImagePreviews.push(reader.result);
        if (newImagePreviews.length === validFiles.length) {
          setImagePreviews(prev => [...prev, ...newImagePreviews]);
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, ...validFiles]
          }));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    const newPreviews = [...imagePreviews];
    newPreviews.splice(index, 1);
    setImagePreviews(newPreviews);
    
    const newImages = [...formData.images];
    newImages.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      
      // Add all form fields to FormData
      Object.keys(formData).forEach(key => {
        if (key === 'logo' || key === 'images') return; // Handle files separately
        if (key === 'phone') {
          formDataToSend.append('phone', JSON.stringify(formData.phone));
        } else if (typeof formData[key] === 'object') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add logo if it's a new file
      if (formData.logo && typeof formData.logo !== 'string') {
        formDataToSend.append('logo', formData.logo);
      }

      // Add new images
      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          if (image instanceof File) {
            formDataToSend.append('images', image);
          }
        });
      }

      await onUpdate(restaurant._id, formDataToSend);
      onClose();
    } catch (err) {
      console.error('Error updating restaurant:', err);
      setError('خطا در به‌روزرسانی رستوران. لطفاً دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  if (!restaurant) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>ویرایش رستوران: {restaurant.name}</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="edit-restaurant-form">
          <div className="form-section">
            <h3>اطلاعات اصلی</h3>
            <div className="form-group">
              <label>نام رستوران</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label>دسته‌بندی</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                <option value="">انتخاب کنید</option>
                <option value="classic">کلاسیک</option>
                <option value="fastfood">فست فود</option>
                <option value="cafe">کافه</option>
                <option value="vitamin">ویتامین</option>
                <option value="foreign">خارجی</option>
                <option value="vegetarian">گیاهی</option>
                <option value="seafood">غذای دریایی</option>
              </select>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>شهر</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>شناسه پرداخت (Pay ID)</label>
                <input
                  type="text"
                  name="payId"
                  value={formData.payId}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>آدرس</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                required
              />
            </div>
            
            <div className="form-group">
              <label>تلفن‌ها</label>
              {formData.phone.map((phone, index) => (
                <div key={index} className="phone-input-group">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => handlePhoneChange(index, e.target.value)}
                    required
                  />
                  {formData.phone.length > 1 && (
                    <button
                      type="button"
                      className="remove-phone"
                      onClick={() => removePhoneField(index)}
                    >
                      حذف
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addPhoneField}
                className="add-phone"
              >
                + افزودن شماره تلفن
              </button>
            </div>
            
            <div className="form-group">
              <label>توضیحات کوتاه</label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
            
            <div className="form-group">
              <label>اسلاگ (Slug)</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-row">
              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                <label htmlFor="isActive">فعال</label>
              </div>
              
              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="isOpen"
                  name="isOpen"
                  checked={formData.isOpen}
                  onChange={handleInputChange}
                />
                <label htmlFor="isOpen">باز است</label>
              </div>
              
              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="hasCafe"
                  name="hasCafe"
                  checked={formData.hasCafe}
                  onChange={handleInputChange}
                />
                <label htmlFor="hasCafe">دارای کافه</label>
              </div>
              
              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="innerDelivery"
                  name="innerDelivery"
                  checked={formData.innerDelivery}
                  onChange={handleInputChange}
                />
                <label htmlFor="innerDelivery">تحویل داخلی</label>
              </div>
              
              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="valueAdded"
                  name="valueAdded"
                  checked={formData.valueAdded}
                  onChange={handleInputChange}
                />
                <label htmlFor="valueAdded">ارزش افزوده</label>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3>تصاویر</h3>
            
            <div className="form-group">
              <label>لوگو (حداکثر ۳۰۰ کیلوبایت)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
              />
              {logoPreview && (
                <div className="image-preview">
                  <img src={logoPreview} alt="لوگوی رستوران" />
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label>تصاویر رستوران (حداکثر ۱۰ تصویر - هر تصویر حداکثر ۵۰۰ کیلوبایت)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImagesChange}
              />
              <div className="image-previews">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={preview} alt={`تصویر ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() => removeImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-button"
              disabled={loading}
            >
              انصراف
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRestaurantModal;
